import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { getUserWorkspaces } from "@/lib/api/workspaces";
import { Card } from "@/components/ui/card";
import { workspaceSchema } from "@/lib/validations/onboarding";
import { AlertCircle, CheckCircle2, XCircle, Loader2, Upload, Building2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { WorkspaceListSkeleton } from "./skeletons/WorkspaceSkeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface StepWorkspaceProps {
  onComplete: (workspaceId: string) => void;
}

export const StepWorkspace = ({ onComplete }: StepWorkspaceProps) => {
  const [companyName, setCompanyName] = useState("");
  const [companyDomain, setCompanyDomain] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [existingWorkspaces, setExistingWorkspaces] = useState<any[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(true);
  const [validationError, setValidationError] = useState<string>("");
  const [nameSuggestions, setNameSuggestions] = useState<string[]>([]);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState<'available' | 'unavailable' | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    async function loadWorkspaces() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const workspaces = await getUserWorkspaces(user.id);
        setExistingWorkspaces(workspaces);
        
        // If no workspaces, show create form immediately
        if (workspaces.length === 0) {
          setShowCreateForm(true);
        }
      } catch (error) {
        console.error('Error loading workspaces:', error);
        setShowCreateForm(true);
      } finally {
        setLoadingWorkspaces(false);
      }
    }

    loadWorkspaces();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && !isLoading && companyName.trim()) {
        e.preventDefault();
        handleSubmit();
      }
    };

    if (showCreateForm) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isLoading, companyName, showCreateForm]);

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const generateNameSuggestions = (originalName: string): string[] => {
    const suggestions: string[] = [];
    const baseName = originalName.trim();
    
    // Numeric suggestions
    for (let i = 2; i <= 4; i++) {
      suggestions.push(`${baseName}-${i}`);
    }
    
    // Descriptive suffixes
    const suffixes = ['team', 'workspace', 'group'];
    suffixes.forEach(suffix => {
      suggestions.push(`${baseName}-${suffix}`);
    });
    
    return suggestions.slice(0, 5); // Return max 5 suggestions
  };

  const checkSlugAvailability = useCallback(async (name: string) => {
    if (!name.trim()) {
      setAvailabilityStatus(null);
      return;
    }

    const slug = generateSlug(name);
    setCheckingAvailability(true);
    setAvailabilityStatus(null);

    try {
      const { data, error } = await supabase.functions.invoke('check-workspace-slug', {
        body: { slug }
      });

      if (error) {
        console.error('Error checking slug availability:', error);
        setAvailabilityStatus(null);
        return;
      }

      setAvailabilityStatus(data.available ? 'available' : 'unavailable');
      
      if (!data.available) {
        const suggestions = generateNameSuggestions(name);
        setNameSuggestions(suggestions);
      } else {
        setNameSuggestions([]);
      }
    } catch (error) {
      console.error('Error checking availability:', error);
      setAvailabilityStatus(null);
    } finally {
      setCheckingAvailability(false);
    }
  }, []);

  // Debounce the availability check
  useEffect(() => {
    if (!showCreateForm || !companyName.trim()) {
      return;
    }

    const timeoutId = setTimeout(() => {
      checkSlugAvailability(companyName);
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timeoutId);
  }, [companyName, showCreateForm, checkSlugAvailability]);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Ungültiger Dateityp",
        description: "Bitte wähle ein Bild aus.",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: "Datei zu groß",
        description: "Das Bild darf maximal 2MB groß sein.",
        variant: "destructive",
      });
      return;
    }

    setAvatarFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const uploadAvatar = async (workspaceId: string): Promise<string | null> => {
    if (!avatarFile) return null;

    setUploadingAvatar(true);
    try {
      const fileExt = avatarFile.name.split('.').pop();
      const fileName = `${workspaceId}-${Date.now()}.${fileExt}`;
      const filePath = `${workspaceId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('workspace-logos')
        .upload(filePath, avatarFile, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('workspace-logos')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast({
        title: "Upload-Fehler",
        description: "Das Logo konnte nicht hochgeladen werden.",
        variant: "destructive",
      });
      return null;
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSubmit = async () => {
    setValidationError("");
    
    // Validate input
    const validation = workspaceSchema.safeParse({ name: companyName });
    if (!validation.success) {
      const errorMessage = validation.error.errors[0]?.message || "Ungültige Eingabe";
      setValidationError(errorMessage);
      toast({
        title: "Validierungsfehler",
        description: errorMessage,
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        console.error("Session error:", sessionError);
        throw new Error("Keine gültige Session. Bitte melde dich erneut an.");
      }

      const user = session.user;
      const slug = generateSlug(companyName);

      console.log("Calling create-workspace function...");

      const { data, error } = await supabase.functions.invoke("create-workspace", {
        body: {
          name: companyName,
          slug,
          domain: companyDomain || null,
          user_id: user.id,
        },
      });

      if (error) {
        console.error("Function invocation error:", error);
        
        // Check if it's a duplicate slug error
        if (error.message?.includes('DUPLICATE_SLUG') || error.message?.includes('bereits vergeben')) {
          const suggestions = generateNameSuggestions(companyName);
          setNameSuggestions(suggestions);
          const errorMsg = "Dieser Workspace-Name ist bereits vergeben. Bitte wähle einen anderen Namen.";
          setValidationError(errorMsg);
          toast({
            title: "Name bereits vergeben",
            description: "Wähle einen der vorgeschlagenen Namen oder gib einen anderen ein.",
            variant: "destructive",
          });
          return;
        }
        
        throw error;
      }

      console.log("Workspace created via function:", data.workspace_id);

      // Upload avatar if provided
      if (avatarFile) {
        const logoUrl = await uploadAvatar(data.workspace_id);
        
        if (logoUrl) {
          // Update workspace with logo URL
          const { error: updateError } = await supabase
            .from('workspaces')
            .update({ 
              branding_settings: { 
                logo_url: logoUrl 
              } 
            })
            .eq('id', data.workspace_id);

          if (updateError) {
            console.error('Error updating workspace with logo:', updateError);
          }
        }
      }

      toast({
        title: "Erfolg",
        description: "Workspace wurde erfolgreich erstellt.",
      });

      onComplete(data.workspace_id);
    } catch (error: any) {
      console.error("Error creating workspace:", error);
      toast({
        title: "Fehler",
        description: error.message || "Workspace konnte nicht erstellt werden. Bitte versuche es erneut.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (loadingWorkspaces) {
    return <WorkspaceListSkeleton />;
  }

  // Show workspace selection if user has workspaces
  if (!showCreateForm && existingWorkspaces.length > 0) {
    return (
      <div className="space-y-10 animate-fade-in">
        <div>
          <h1 className="text-2xl md:text-[40px] leading-[1.1] tracking-tight font-bold font-serif text-foreground">
            Welchen Workspace möchtest du nutzen?
          </h1>
          <p className="mt-4 text-base md:text-lg text-muted-foreground">
            Wähle einen bestehenden Workspace oder erstelle einen neuen.
          </p>
        </div>

        <div className="space-y-4">
          {existingWorkspaces.map((workspace) => (
            <Card
              key={workspace.id}
              className="p-6 cursor-pointer hover:border-primary transition-colors"
              onClick={() => onComplete(workspace.id)}
            >
              <div className="flex items-center gap-4">
                <Avatar className="h-12 w-12">
                  <AvatarImage src={workspace.branding_settings?.logo_url} />
                  <AvatarFallback>
                    <Building2 className="h-6 w-6 text-muted-foreground" />
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-foreground">
                    {workspace.name}
                  </h3>
                  {workspace.domain && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {workspace.domain}
                    </p>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>

        <Button
          variant="outline"
          onClick={() => setShowCreateForm(true)}
          className="w-full h-12"
        >
          Neuen Workspace erstellen
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-10 animate-fade-in">
      <div>
        <h1 className="text-2xl md:text-[40px] leading-[1.1] tracking-tight font-bold font-serif text-foreground">
          Richte deinen Quentari-Workspace ein
        </h1>
        <p className="mt-4 text-base md:text-lg text-muted-foreground">
          Ein Workspace ist der zentrale Ort für dein Team.
        </p>
      </div>

      <div className="space-y-6">
        {validationError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{validationError}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <Label>Workspace-Logo (optional)</Label>
          <div className="flex items-center gap-4">
            <Avatar className="h-20 w-20">
              <AvatarImage src={avatarPreview || undefined} />
              <AvatarFallback>
                <Building2 className="h-10 w-10 text-muted-foreground" />
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <input
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                className="hidden"
                id="avatar-upload"
              />
              <Label
                htmlFor="avatar-upload"
                className="inline-flex items-center justify-center gap-2 h-10 px-4 py-2 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground cursor-pointer transition-colors"
              >
                <Upload className="h-4 w-4" />
                {avatarFile ? 'Anderes Bild wählen' : 'Logo hochladen'}
              </Label>
              <p className="text-sm text-muted-foreground mt-2">
                JPG, PNG oder GIF, max. 2MB
              </p>
            </div>
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="companyName">Firmenname *</Label>
          <div className="relative">
            <Input
              id="companyName"
              value={companyName}
              onChange={(e) => {
                setCompanyName(e.target.value);
                setValidationError("");
                setAvailabilityStatus(null);
              }}
              placeholder="z.B. Acme GmbH"
              className="h-12 pr-10"
              maxLength={100}
            />
            {companyName.trim() && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {checkingAvailability ? (
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                ) : availabilityStatus === 'available' ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : availabilityStatus === 'unavailable' ? (
                  <XCircle className="h-5 w-5 text-destructive" />
                ) : null}
              </div>
            )}
          </div>
          {availabilityStatus === 'available' && (
            <div className="flex items-center gap-2 text-sm text-green-600 animate-fade-in">
              <CheckCircle2 className="h-4 w-4" />
              <p>Dieser Name ist verfügbar</p>
            </div>
          )}
          {availabilityStatus === 'unavailable' && (
            <div className="flex items-center gap-2 text-sm text-destructive animate-fade-in">
              <XCircle className="h-4 w-4" />
              <p>Dieser Name ist bereits vergeben</p>
            </div>
          )}
          {nameSuggestions.length > 0 && (
            <div className="space-y-2 animate-fade-in">
              <p className="text-sm text-muted-foreground">
                Vorgeschlagene Alternativen:
              </p>
              <div className="flex flex-wrap gap-2">
                {nameSuggestions.map((suggestion) => (
                  <Button
                    key={suggestion}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setCompanyName(suggestion);
                      setNameSuggestions([]);
                      setValidationError("");
                      setAvailabilityStatus(null);
                    }}
                    className="h-9 text-sm"
                  >
                    {suggestion}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="companyDomain">Firmen-Domain (optional)</Label>
          <Input
            id="companyDomain"
            value={companyDomain}
            onChange={(e) => setCompanyDomain(e.target.value)}
            placeholder="z.B. acme.com"
            className="h-12"
          />
          <p className="text-sm text-muted-foreground">
            Wird für Team-Einladungen verwendet
          </p>
        </div>

        <Button
          onClick={handleSubmit}
          disabled={isLoading || availabilityStatus === 'unavailable' || checkingAvailability || uploadingAvatar}
          className="h-12 px-8 text-base font-semibold w-full sm:w-auto"
        >
          {isLoading || uploadingAvatar ? "Wird erstellt..." : "Workspace erstellen"}
        </Button>
      </div>
    </div>
  );
};
